import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import { moodboardService } from '../services/moodboardService';
import { tagService } from '../services/tagService';
import aiService from '../services/aiService';
import { Moodboard } from '../types/moodboard';
import { Tag } from '../types/tag';
import TagSelector from './shared/TagSelector';
import { Button, Card, Modal } from '../components/ui';

// Page-load entrance variants: base=300ms, slow=500ms — numeric equivalents of
// --snapfy-duration-base and --snapfy-duration-slow; easing mirrors --snapfy-ease-standard.
const containerVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const } },
} as const;

const MoodboardsPage: React.FC = () => {
    const { t } = useTranslation('moodboards');
    const navigate = useNavigate();
    const prefersReducedMotion = useReducedMotion();

    const [moodboards, setMoodboards] = useState<Moodboard[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Tag filter
    const [allTags, setAllTags] = useState<Tag[]>([]);
    const [filterTagIds, setFilterTagIds] = useState<number[]>([]);

    // Inline edit
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');
    const editInputRef = useRef<HTMLInputElement>(null);

    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState<Moodboard | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Tag management (standalone modal)
    const [tagModalTarget, setTagModalTarget] = useState<Moodboard | null>(null);
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [isSavingTags, setIsSavingTags] = useState(false);

    // Export as brandbook
    const [exportingId, setExportingId] = useState<number | null>(null);

    // Drag reorder
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const fetchMoodboards = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await moodboardService.getMoodboards();
            setMoodboards(data);
        } catch {
            toast.error(t('errorLoading'));
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchMoodboards();
        tagService.getTags().then(setAllTags).catch(() => {});
    }, [fetchMoodboards]);

    useEffect(() => {
        if (editingId !== null && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingId]);

    // Collect all unique tags used in moodboards for the filter bar
    const usedTags = useMemo(() => {
        const tagMap = new Map<number, Tag>();
        moodboards.forEach(m => m.tags?.forEach(tag => tagMap.set(tag.id, tag)));
        // Also include allTags so user can filter by tags not yet assigned
        allTags.forEach(tag => tagMap.set(tag.id, tag));
        return Array.from(tagMap.values());
    }, [moodboards, allTags]);

    // Filtering: text search + tag filter
    const filteredMoodboards = useMemo(() => {
        let result = moodboards;

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(m =>
                m.name.toLowerCase().includes(term) ||
                m.stylePrompt?.toLowerCase().includes(term) ||
                m.tags?.some(tag => tag.name.toLowerCase().includes(term))
            );
        }

        if (filterTagIds.length > 0) {
            result = result.filter(m =>
                filterTagIds.every(fid => m.tags?.some(tag => tag.id === fid))
            );
        }

        return result;
    }, [moodboards, searchTerm, filterTagIds]);

    // Toggle tag filter
    const toggleFilterTag = (tagId: number) => {
        setFilterTagIds(prev =>
            prev.includes(tagId)
                ? prev.filter(id => id !== tagId)
                : [...prev, tagId]
        );
    };

    // Inline rename
    const startEditing = (m: Moodboard) => {
        setEditingId(m.id);
        setEditValue(m.name);
    };

    const saveEdit = async () => {
        if (editingId === null || !editValue.trim()) return;
        try {
            const updated = await moodboardService.updateMoodboard(editingId, { name: editValue.trim() });
            setMoodboards(prev => prev.map(m => m.id === updated.id ? updated : m));
            toast.success(t('updated'));
        } catch {
            toast.error(t('errorUpdating'));
        } finally {
            setEditingId(null);
        }
    };

    const handleEditKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') saveEdit();
        if (e.key === 'Escape') setEditingId(null);
    };

    // Delete
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            await moodboardService.deleteMoodboard(deleteTarget.id);
            setMoodboards(prev => prev.filter(m => m.id !== deleteTarget.id));
            toast.success(t('deleted'));
        } catch {
            toast.error(t('errorDeleting'));
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    };

    // Tag management (standalone)
    const openTagModal = (m: Moodboard) => {
        setTagModalTarget(m);
        setSelectedTagIds(m.tags?.map(tag => tag.id) || []);
    };

    const saveTags = async () => {
        if (!tagModalTarget) return;
        setIsSavingTags(true);
        try {
            const updated = await moodboardService.syncTags(tagModalTarget.id, selectedTagIds);
            setMoodboards(prev => prev.map(m => m.id === updated.id ? updated : m));
            toast.success(t('tagsUpdated'));
        } catch {
            toast.error(t('errorTags'));
        } finally {
            setIsSavingTags(false);
            setTagModalTarget(null);
        }
    };

    // Export as Brandbook (with AI analysis)
    const handleExportAsBrandbook = async (moodboard: Moodboard) => {
        if (exportingId) return;
        setExportingId(moodboard.id);
        const toastId = toast.loading(t('exportingAnalyzing'));
        try {
            const { taskId } = await moodboardService.exportAsBrandbook(moodboard.id);

            // Poll until analysis completes
            const result = await aiService.pollTask(taskId);

            if (result.status === 'completed' || result.status === 'image_generated') {
                toast.success(t('exportedAsBrandbook'), { id: toastId });
            } else {
                toast.error(t('errorExporting') + ': ' + (result.errorMessage || 'Unknown error'), { id: toastId });
            }
        } catch (err) {
            console.error('Export as Brandbook failed:', err);
            toast.error(t('errorExporting'), { id: toastId });
        } finally {
            setExportingId(null);
        }
    };

    // Drag & Drop reorder
    const handleDragStart = (index: number) => setDragIndex(index);
    const handleDragOver = (e: React.DragEvent, index: number) => { e.preventDefault(); setDragOverIndex(index); };
    const handleDragEnd = () => { setDragIndex(null); setDragOverIndex(null); };

    const handleDrop = async (targetIndex: number) => {
        if (dragIndex === null || dragIndex === targetIndex) {
            setDragIndex(null);
            setDragOverIndex(null);
            return;
        }
        const reordered = [...moodboards];
        const [moved] = reordered.splice(dragIndex, 1);
        reordered.splice(targetIndex, 0, moved);
        setMoodboards(reordered);
        setDragIndex(null);
        setDragOverIndex(null);
        try {
            const items = reordered.map((m, i) => ({ id: m.id, sortOrder: i }));
            await moodboardService.reorderMoodboards(items);
            toast.success(t('reordered'));
        } catch {
            fetchMoodboards();
        }
    };

    return (
        <motion.div
            className="min-h-screen bg-snapfy-bg p-6 md:p-8"
            variants={prefersReducedMotion ? undefined : containerVariants}
            initial={prefersReducedMotion ? undefined : 'hidden'}
            animate={prefersReducedMotion ? undefined : 'visible'}
        >
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-snapfy-text-primary">{t('title')}</h1>
                    <p className="text-snapfy-text-secondary mt-1">{t('subtitle')}</p>
                </div>
                <Button
                    variant="solid"
                    onClick={() => navigate('/moodboard')}
                    className="self-start"
                    leadingIcon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                    }
                >
                    {t('createFirst')}
                </Button>
            </div>

            {/* Search + Tag Filter */}
            <div className="mb-6 space-y-3">
                <div className="relative max-w-md">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-snapfy-text-secondary z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {/* Native input kept: Input primitive lacks forwardRef; search field needs
                        an absolute leading-icon overlay. Restyled with token classes. */}
                    <input
                        type="text"
                        placeholder={t('search')}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-snapfy-inner border border-snapfy-border rounded-input text-snapfy-text-primary placeholder:text-snapfy-text-secondary focus:ring-snapfy-focus focus:border-snapfy-primary focus:outline-none transition-[box-shadow,border-color] duration-150"
                    />
                </div>

                {/* Tag filter pills */}
                {usedTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs text-snapfy-text-secondary uppercase tracking-wider mr-1">{t('filterByTags')}:</span>
                        {usedTags.map(tag => (
                            <button
                                key={tag.id}
                                onClick={() => toggleFilterTag(tag.id)}
                                className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                                    filterTagIds.includes(tag.id)
                                        ? 'border-snapfy-primary bg-snapfy-primary/20 text-snapfy-accent-soft'
                                        : 'border-snapfy-border bg-snapfy-surface-hover/50 text-snapfy-text-secondary hover:border-snapfy-border'
                                }`}
                            >
                                <span
                                    className="inline-block w-2 h-2 rounded-full mr-1"
                                    style={{ backgroundColor: tag.color }}
                                />
                                {tag.name}
                            </button>
                        ))}
                        {filterTagIds.length > 0 && (
                            <button
                                onClick={() => setFilterTagIds([])}
                                className="px-2 py-1 text-xs text-snapfy-text-secondary hover:text-snapfy-text-secondary transition-colors"
                            >
                                {t('clearFilters')}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Loading */}
            {isLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="bg-snapfy-inner rounded-card p-6 animate-pulse h-48" />
                    ))}
                </div>
            )}

            {/* Empty state */}
            {!isLoading && filteredMoodboards.length === 0 && (
                <div className="text-center py-20">
                    <svg className="w-16 h-16 text-snapfy-text-secondary mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-snapfy-text-secondary text-lg">{t('empty')}</p>
                    <p className="text-snapfy-text-secondary mt-2">{t('emptyDescription')}</p>
                    <Button
                        variant="solid"
                        onClick={() => navigate('/moodboard')}
                        className="mt-6"
                    >
                        {t('createFirst')}
                    </Button>
                </div>
            )}

            {/* Moodboard Grid */}
            {!isLoading && filteredMoodboards.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredMoodboards.map((moodboard, index) => (
                        <Card
                            key={moodboard.id}
                            interactive
                            className={`group relative overflow-visible p-0 transition-all ${
                                dragOverIndex === index
                                    ? 'border-snapfy-primary scale-[1.02]'
                                    : ''
                            } ${dragIndex === index ? 'opacity-50' : ''}`}
                            // Card does not forward drag props — wrap contents in a div for DnD
                        >
                            {/* DnD wrapper — preserves all drag handlers */}
                            <div
                                draggable={!searchTerm && filterTagIds.length === 0}
                                onDragStart={() => handleDragStart(index)}
                                onDragOver={e => handleDragOver(e, index)}
                                onDrop={() => handleDrop(index)}
                                onDragEnd={handleDragEnd}
                                className="h-full"
                            >
                                {/* Thumbnail grid — clickable to open detail */}
                                <div className="cursor-pointer" onClick={() => navigate(`/moodboard?edit=${moodboard.id}`)}>
                                    {moodboard.moodboardImagesUrls && moodboard.moodboardImagesUrls.length > 0 ? (
                                        <div className="grid grid-cols-2 gap-0.5 rounded-t-card overflow-hidden h-40">
                                            {moodboard.moodboardImagesUrls.slice(0, 4).map((url, i) => (
                                                <div key={i} className="relative overflow-hidden bg-snapfy-inner">
                                                    <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                                                </div>
                                            ))}
                                            {moodboard.moodboardImagesUrls.length < 4 &&
                                                [...Array(4 - moodboard.moodboardImagesUrls.length)].map((_, i) => (
                                                    <div key={`empty-${i}`} className="bg-snapfy-inner" />
                                                ))
                                            }
                                        </div>
                                    ) : (
                                        <div className="h-40 rounded-t-card bg-gradient-to-br from-[#1a0a3e]/30 to-snapfy-surface flex items-center justify-center">
                                            {/* #1a0a3e: sanctioned residual — gradient stop (Phase 7 owns) */}
                                            <svg className="w-12 h-12 text-snapfy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="p-4">
                                    {editingId === moodboard.id ? (
                                        <div className="flex items-center gap-2 mb-2">
                                            {/* Native input kept: Input primitive lacks forwardRef;
                                                editInputRef.current.focus()/select() required for
                                                inline rename UX. Restyled with token classes. */}
                                            <input
                                                ref={editInputRef}
                                                value={editValue}
                                                onChange={e => setEditValue(e.target.value)}
                                                onKeyDown={handleEditKeyDown}
                                                onBlur={saveEdit}
                                                className="flex-1 bg-snapfy-inner border border-snapfy-primary rounded-input px-2 py-1 text-snapfy-text-primary text-sm focus:outline-none focus:ring-snapfy-focus"
                                                maxLength={255}
                                            />
                                        </div>
                                    ) : (
                                        <h3
                                            className="text-snapfy-text-primary font-semibold text-lg mb-1 truncate cursor-pointer hover:text-snapfy-accent-soft transition-colors"
                                            onClick={() => navigate(`/moodboard?edit=${moodboard.id}`)}
                                        >
                                            {moodboard.name}
                                        </h3>
                                    )}

                                    {/* Description preview */}
                                    {moodboard.stylePrompt && moodboard.stylePrompt !== 'Moodboard visual' && (
                                        <p className="text-snapfy-text-secondary text-xs mb-2 line-clamp-2">{moodboard.stylePrompt}</p>
                                    )}

                                    {/* Color palette preview */}
                                    {moodboard.colorPalette && moodboard.colorPalette.length > 0 && (
                                        <div className="flex items-center gap-1 mb-2">
                                            {moodboard.colorPalette.slice(0, 6).map((color, i) => (
                                                <div
                                                    key={i}
                                                    className="w-5 h-5 rounded-full border border-snapfy-border"
                                                    style={{ backgroundColor: typeof color === 'string' ? color : (color as any)?.hexCode || '#666' }}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {/* Tags */}
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {moodboard.tags?.map(tag => (
                                            <span
                                                key={tag.id}
                                                className="px-2 py-0.5 text-xs rounded-full border"
                                                style={{
                                                    backgroundColor: `${tag.color}20`,
                                                    borderColor: `${tag.color}50`,
                                                    color: tag.color,
                                                }}
                                            >
                                                {tag.name}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Meta */}
                                    <div className="flex items-center gap-3 text-xs text-snapfy-text-secondary mb-3">
                                        {moodboard.moodboardImagesUrls && moodboard.moodboardImagesUrls.length > 0 && (
                                            <span>{moodboard.moodboardImagesUrls.length} {t('images')}</span>
                                        )}
                                        <span>{new Date(moodboard.createdAt).toLocaleDateString()}</span>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Button
                                            variant="ghost"
                                            onClick={() => navigate(`/moodboard?edit=${moodboard.id}`)}
                                            className="px-3 py-1.5 text-sm normal-case tracking-normal font-medium"
                                        >
                                            {t('viewEdit')}
                                        </Button>
                                        <button
                                            onClick={() => handleExportAsBrandbook(moodboard)}
                                            disabled={exportingId !== null}
                                            className="px-3 py-1.5 text-sm bg-green-600/20 hover:bg-green-600/30 text-green-300 rounded-input transition-colors disabled:opacity-50 flex items-center gap-1.5"
                                        >
                                            {/* Export-as-Brandbook: green brand color, no token exists; kept native + green-* Tailwind classes */}
                                            {exportingId === moodboard.id && (
                                                <div className="w-3 h-3 border-2 border-green-300 border-t-transparent rounded-full animate-spin" />
                                            )}
                                            {t('exportAsBrandbook')}
                                        </button>
                                        <button
                                            onClick={() => setDeleteTarget(moodboard)}
                                            className="p-1.5 text-snapfy-text-secondary hover:text-snapfy-error hover:bg-snapfy-error/20 rounded-input transition-colors ml-auto"
                                            title={t('delete')}
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Drag handle */}
                                {!searchTerm && filterTagIds.length === 0 && (
                                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing bg-black/60 rounded p-1">
                                        <svg className="w-4 h-4 text-snapfy-text-secondary" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M8 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm8-16a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Delete Confirmation Modal — explicit-action close only (no scrim/Esc dismiss during a destructive op) */}
            <Modal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                closeOnEsc={false}
                closeOnOverlayClick={false}
                title={t('deleteConfirmTitle')}
                footer={
                    <>
                        <Button
                            variant="ghost"
                            onClick={() => setDeleteTarget(null)}
                            disabled={isDeleting}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="solid"
                            onClick={confirmDelete}
                            disabled={isDeleting}
                            isLoading={isDeleting}
                            className="bg-snapfy-error hover:bg-snapfy-error/80"
                        >
                            {t('deleteConfirmButton')}
                        </Button>
                    </>
                }
            >
                <p className="text-snapfy-text-secondary">
                    {deleteTarget && t('deleteConfirmMessage', { name: deleteTarget.name })}
                </p>
            </Modal>

            {/* Tag Management Modal (standalone) — explicit-action close only (avoid losing unsaved tag selection) */}
            <Modal
                isOpen={!!tagModalTarget}
                onClose={() => setTagModalTarget(null)}
                closeOnEsc={false}
                closeOnOverlayClick={false}
                title={t('manageTags')}
                footer={
                    <>
                        <Button
                            variant="ghost"
                            onClick={() => setTagModalTarget(null)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="solid"
                            onClick={saveTags}
                            disabled={isSavingTags}
                            isLoading={isSavingTags}
                        >
                            {t('save')}
                        </Button>
                    </>
                }
            >
                <p className="text-snapfy-text-secondary text-sm mb-4">{tagModalTarget?.name}</p>
                <div className="mb-2">
                    <TagSelector
                        selectedTags={selectedTagIds}
                        onChange={setSelectedTagIds}
                        placeholder="Buscar ou criar tags..."
                        allowCreate
                    />
                </div>
            </Modal>
        </motion.div>
    );
};

export default MoodboardsPage;
